import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, HStack, Text, useColorModeValue, VStack } from '@bonusly/design-system';

import { EmployeeSpotlight } from '../../../schemas/spotlight';
import { formatUserSpotlightDateRange } from './user-spotlight-date-range';

interface UserSpotlightIntroCardProps {
	spotlight: EmployeeSpotlight;
	onExplore: () => void;
	isActive: boolean;
	fallbackUserName?: string;
}

interface StatItemProps {
	label: string;
	value: string | number;
	color: string;
}

const StatItem: FC<StatItemProps> = ({ label, value, color }) => (
	<VStack spacing={1} align="center" minW="100px">
		<Text fontSize="xs" fontWeight="medium" color={color} textTransform="uppercase" letterSpacing="wide">
			{label}
		</Text>
		<Text fontFamily="'Plus Jakarta Sans', sans-serif" fontSize={{ base: '3xl', md: '4xl' }} fontWeight="bold" color={color} lineHeight="1">
			{value}
		</Text>
	</VStack>
);

export const UserSpotlightIntroCard: FC<UserSpotlightIntroCardProps> = ({ spotlight, onExplore, isActive, fallbackUserName }) => {
	const { t, i18n } = useTranslation('spotlight');
	const userName = spotlight.user?.displayName || fallbackUserName || t('viewer.header.defaultName');
	// High contrast text color for stats and disclaimer - dark in light mode, light in dark mode
	const highContrastColor = useColorModeValue('gray.900', 'gray.25');

	// Get months at company from spotlight (pre-calculated on backend)
	const monthsAtBonusly = spotlight.monthsAtCompany ?? 0;

	// Get stats from top-level spotlight fields
	const stats = useMemo(() => {
		return {
			recognitionReceived: spotlight.totalReceivedCount ?? 0,
			recognitionGiven: spotlight.totalGivenCount ?? 0,
		};
	}, [spotlight.totalReceivedCount, spotlight.totalGivenCount]);

	// Generate a tagline based on the sections
	const tagline = useMemo(() => {
		// Priority order: superpowers, promotion, hidden_talent, top_values
		const taglineSectionTypes = ['superpowers', 'promotion', 'hidden_talent', 'top_values'];

		// Find first section matching one of the tagline types
		for (const sectionType of taglineSectionTypes) {
			const section = spotlight.sections.find((s) => s.type === sectionType);
			if (section?.title) {
				return section.title;
			}
		}

		// Return blank if none found
		return '';
	}, [spotlight]);

	if (!isActive) {
		return null;
	}

	return (
		<Box
			opacity={isActive ? 1 : 0}
			visibility={isActive ? 'visible' : 'hidden'}
			pointerEvents={isActive ? 'auto' : 'none'}
			transition="opacity 0.3s ease, visibility 0.3s ease"
			width="100%"
		>
			<VStack spacing={{ base: 3, md: 8 }} align="center" py={{ base: 4, md: 16 }}>
				{/* Celebrating badge */}
				<Box as="span" display="inline-block" px={5} py={2} borderRadius="full" fontSize="sm" fontWeight="semibold" bg="success.bg" color="success.text">
					{t('intro.celebratingBadge')}
				</Box>

				{/* User name + date range */}
				<VStack spacing={2} align="center">
					<Text
						fontFamily="'Plus Jakarta Sans', sans-serif"
						fontSize={{ base: '4xl', md: '6xl' }}
						fontWeight="bold"
						color="text.default"
						textAlign="center"
						lineHeight="shorter"
					>
						{userName}
					</Text>
					<Text fontSize="md" fontWeight="medium" color="text.soft" textAlign="center">
						{formatUserSpotlightDateRange(spotlight.startTime, spotlight.endTime, i18n.language)}
					</Text>
				</VStack>

				{/* Tagline */}
				{tagline && (
					<Text
						fontFamily="'Plus Jakarta Sans', sans-serif"
						fontSize={{ base: 'xl', md: '2xl' }}
						fontWeight="medium"
						color="text.default"
						textAlign="center"
						maxW="500px"
					>
						{tagline}
					</Text>
				)}

				{/* Stats */}
				<HStack spacing={{ base: 6, md: 12 }} pt={{ base: 4, md: 8 }} pb={{ base: 2, md: 4 }} flexWrap="wrap" justify="center">
					<StatItem label={t('intro.stats.recognitionReceived')} value={stats.recognitionReceived} color={highContrastColor} />
					<StatItem label={t('intro.stats.recognitionGiven')} value={stats.recognitionGiven} color={highContrastColor} />
					<StatItem label={t('intro.stats.monthsAtBonusly')} value={monthsAtBonusly ?? 0} color={highContrastColor} />
				</HStack>

				{/* Explore button */}
				<Button
					onClick={onExplore}
					bg="secondary.bg"
					color="secondary.fgOnSurface"
					size="lg"
					borderRadius="full"
					px={8}
					py={6}
					fontSize="md"
					fontWeight="semibold"
					mt={{ base: 2, md: 4 }}
					_hover={{
						transform: 'scale(1.02)',
						opacity: 0.9,
					}}
					transition="all 0.2s ease"
				>
					{t('intro.exploreButton')}
				</Button>

				{/* AI disclaimer */}
				<Text fontSize="xs" color={highContrastColor} textAlign="center">
					{t('intro.aiDisclaimer')}
				</Text>
			</VStack>
		</Box>
	);
};
